import { Injectable, inject } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivateChild,
    Router,
    RouterStateSnapshot,
    UrlTree,
} from '@angular/router';
import { MessageService } from 'primeng/api';
import { Store } from '@ngrx/store';

import { HOME_ROUTE, NO_ACCESS_ROUTE } from '../constant/route.constant';
import { IAppState } from '../../store/interface/app.state.interface';
import { WRITING_SEGMENTS } from '../../shared/constant/permission.constant';
import { EntityMode } from '../../shared/enum/entity-mode.enum';
import { PermissionKey } from '../../shared/enum/permission-key.enum';
import { PermissionLevel } from '../../shared/enum/permission-level.enum';
import { holdsPermission, refusalReasonFor } from '../../shared/utils/permission.util';
import { selectLoggedInUser } from '../../store/user/user.selectors';

// Runs after SignedInGuard has the person. A business without access sees only the no-access
// page; a page with `data.permission` needs `view` on it, or `manage` to create or edit.
@Injectable({ providedIn: 'root' })
export class AuthorizationGuard implements CanActivateChild {
    private readonly messageService = inject(MessageService);
    private readonly router = inject(Router);
    private readonly store = inject(Store<IAppState>);
    private readonly loggedInUser = this.store.selectSignal(selectLoggedInUser);

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
        const user = this.loggedInUser();
        const isNoAccessPage = state.url.split('?')[0] === NO_ACCESS_ROUTE;
        if (!user?.hasAccess) return isNoAccessPage || this.router.parseUrl(NO_ACCESS_ROUTE);
        if (isNoAccessPage) return this.router.parseUrl(HOME_ROUTE);

        const permission = route.data['permission'] as PermissionKey | undefined;
        const mode = this.modeOf(state.url);
        const level = mode === EntityMode.VIEW ? PermissionLevel.VIEW : PermissionLevel.MANAGE;
        if (!permission || holdsPermission(user, permission, level)) return true;

        this.messageService.add({
            severity: 'warn',
            summary: 'Not available',
            detail: refusalReasonFor(permission, mode),
        });
        return this.router.parseUrl(HOME_ROUTE);
    }

    // A page that creates or edits names it in its address; any other page only reads.
    private modeOf(url: string): EntityMode {
        const path = url.split('?')[0];
        return WRITING_SEGMENTS.find((segment) => path.includes(`/${segment}`)) ?? EntityMode.VIEW;
    }
}
